import axios from "axios";

const baseURL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Token storage. We keep JWTs in localStorage and send them via the
// Authorization header because the app runs inside a cross-site iframe (the
// Emergent preview), where SameSite cookies are not reliably sent. The backend
// still sets cookies too, but the Bearer header is the primary mechanism.
const ACCESS_KEY = "sema_access_token";
const REFRESH_KEY = "sema_refresh_token";

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(access, refresh) {
  if (access) localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

const api = axios.create({
  baseURL,
  withCredentials: true,
});

// Cold-start resilience: the app runs on a scale-to-zero platform, so the first
// requests after a container wakes can hit transient connection errors / 502/503/
// 504s while the backend boots. Retry those with exponential backoff instead of
// surfacing an instant "Something went wrong".
const MAX_RETRIES = 3;
const RETRY_DELAYS = [500, 1000, 2000];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Attach the access token to every request.
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth endpoints must never trigger the refresh-retry loop.
const AUTH_SKIP = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh",
  "/auth/me",
  "/auth/logout",
];

// Single-flight refresh: concurrent 401s share one /auth/refresh call. Mints a
// new access token from the stored refresh token and persists it.
let refreshPromise = null;

export function refreshSession() {
  if (!refreshPromise) {
    const refreshToken = getRefreshToken();
    refreshPromise = axios
      .post(
        `${baseURL}/auth/refresh`,
        { refresh_token: refreshToken },
        { withCredentials: true }
      )
      .then((res) => {
        const newAccess = res.data?.access_token;
        if (newAccess) setTokens(newAccess, null);
        return res;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthPath = AUTH_SKIP.some((p) => url.includes(p));

    // Access token likely expired — mint a new one from the refresh token and
    // retry the original request exactly once. On refresh failure, broadcast a
    // forced-logout so the AuthContext can redirect to /login.
    if (status === 401 && original && !original._retry && !isAuthPath) {
      original._retry = true;
      try {
        await refreshSession();
        const token = getAccessToken();
        if (token) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${token}`;
        }
        return api(original);
      } catch {
        clearTokens();
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("sema:auth-expired"));
        }
        // We're force-logging-out and redirecting to /login. Return a
        // never-settling promise so callers that didn't attach a .catch don't
        // surface an unhandled-rejection ("Uncaught runtime error") overlay;
        // the pending component unmounts on redirect.
        return new Promise(() => {});
      }
    }

    // Retry transient cold-start failures: connection errors (no response) and
    // 502/503/504. GETs additionally retry on timeouts. Real 4xx/5xx app errors
    // are never retried, and cancellations are propagated untouched. POSTs only
    // retry on the transient set above to avoid duplicate side effects.
    const method = (original?.method || "get").toLowerCase();
    const isGet = method === "get";
    const noResponse = !error.response;
    const transientStatus = status === 502 || status === 503 || status === 504;
    const isTimeout = error.code === "ECONNABORTED";
    const retryable =
      original &&
      !axios.isCancel(error) &&
      (noResponse || transientStatus || (isGet && isTimeout));

    if (retryable) {
      original._retryCount = (original._retryCount || 0) + 1;
      if (original._retryCount <= MAX_RETRIES) {
        await sleep(RETRY_DELAYS[Math.min(original._retryCount - 1, RETRY_DELAYS.length - 1)]);
        return api(original);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Lightweight wake + readiness probe. Uses bare fetch (no auth/retry interceptors)
// and swallows errors — callers just want { ready } or a best-effort container wake.
export async function pingWarm() {
  try {
    const res = await fetch(`${baseURL}/system/warm`, { method: "GET" });
    if (!res.ok) return { ready: false };
    return await res.json();
  } catch {
    return { ready: false };
  }
}
